import { useState } from "react";
import { addMonths, format } from "date-fns";
import { PauseCircle, SkipForward, Repeat, XCircle, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { formatGBP } from "@/lib/api";
import { cn } from "@/lib/utils";

export const fmt = (d) => format(d, "EEEE d MMMM yyyy");

const Shell = ({ open, onOpenChange, icon: Icon, title, desc, children, confirm, onConfirm, danger, testid }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[90vh] overflow-y-auto border-[#2E2E2E] bg-[#111] sm:max-w-lg sm:rounded-3xl" data-testid={`${testid}-dialog`}>
      <DialogHeader>
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#FF6A00]/10 text-[#FF6A00]"><Icon className="h-6 w-6" /></span>
        <DialogTitle className="pt-3 font-display text-2xl">{title}</DialogTitle>
        <DialogDescription className="text-[#A1A1AA]">{desc}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">{children}</div>
      <DialogFooter className="gap-2 sm:gap-2">
        <button className="lc-btn-ghost" onClick={() => onOpenChange(false)} data-testid={`${testid}-back`}>Keep as is</button>
        <button className={cn("lc-btn-primary", danger && "!bg-[#F7F5F0] !text-[#080808] !shadow-none")} onClick={go} disabled={busy} data-testid={`${testid}-confirm`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : confirm}</button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

const Fact = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 rounded-xl bg-[#0a0a0a] px-4 py-3 text-sm">
    <span className="text-[#A1A1AA]">{label}</span>
    <span className="text-right font-semibold" data-testid={testid}>{value}</span>
  </div>
);

export const PauseDialog = ({ open, onOpenChange, sub, onConfirm }) => {
  const [months, setMonths] = useState("1");
  const n = Number(months);
  const resumeOn = addMonths(sub.nextBilling, n);
  return (
    <Shell open={open} onOpenChange={onOpenChange} icon={PauseCircle} testid="pause" title="Pause your subscription"
      desc="Take a break without cancelling. No charges or deliveries while paused." confirm={`Pause for ${n} month${n > 1 ? "s" : ""}`}
      onConfirm={() => onConfirm(n)}>
      <RadioGroup value={months} onValueChange={setMonths} className="grid grid-cols-3 gap-3">
        {["1", "2", "3"].map((m) => (
          <Label key={m} htmlFor={`pause-${m}`} className={cn("cursor-pointer rounded-2xl border p-4 text-center transition-colors", months === m ? "border-[#FF6A00] bg-[#FF6A00]/10" : "border-[#2E2E2E]")} data-testid={`pause-option-${m}`}>
            <RadioGroupItem value={m} id={`pause-${m}`} className="sr-only" />
            <span className="block font-display text-2xl font-bold">{m}</span>
            <span className="text-xs text-[#A1A1AA]">month{m !== "1" && "s"}</span>
          </Label>
        ))}
      </RadioGroup>
      <Fact label="Pause starts" value={fmt(sub.nextBilling)} testid="pause-start-date" />
      <Fact label="Resumes automatically" value={fmt(resumeOn)} testid="pause-resume-date" />
      <p className="text-sm leading-relaxed text-[#F7F5F0]/80" data-testid="pause-impact">
        No deliveries will be made and you won't be charged until <strong>{fmt(resumeOn)}</strong>. We'll email you to confirm.
      </p>
    </Shell>
  );
};

export const SkipDialog = ({ open, onOpenChange, sub, onConfirm }) => (
  <Shell open={open} onOpenChange={onOpenChange} icon={SkipForward} testid="skip" title="Skip your next delivery"
    desc="Your billing and delivery dates move on together, so you're not charged for a skipped delivery." confirm="Skip this delivery" onConfirm={onConfirm}>
    <Fact label="Delivery skipped" value={fmt(sub.nextDelivery)} />
    <Fact label="No charge on" value={fmt(sub.nextBilling)} />
    <Fact label="Next payment" value={fmt(addMonths(sub.nextBilling, 1))} testid="skip-next-payment" />
    <Fact label="Next delivery" value={fmt(addMonths(sub.nextDelivery, 1))} />
  </Shell>
);

export const ChangePlanDialog = ({ open, onOpenChange, sub, plans, onConfirm }) => {
  const current = sub.pendingPlan || sub.plan;
  const [choice, setChoice] = useState(current);
  const sel = plans.find((p) => p.slug === choice);
  return (
    <Shell open={open} onOpenChange={onOpenChange} icon={Repeat} testid="change-plan" title="Change your plan"
      desc="Plan changes apply from your next billing date." confirm={sel && choice !== current ? `Switch to ${sel.name}` : "Choose a new plan"}
      onConfirm={() => choice !== current && onConfirm(choice)}>
      <RadioGroup value={choice} onValueChange={setChoice} className="space-y-3">
        {plans.map((p) => (
          <Label key={p.slug} htmlFor={`cp-${p.slug}`} className={cn("flex cursor-pointer items-center justify-between rounded-2xl border p-4", choice === p.slug ? "border-[#FF6A00] bg-[#FF6A00]/10" : "border-[#2E2E2E]")} data-testid={`change-plan-option-${p.slug}`}>
            <span className="flex items-center gap-3">
              <RadioGroupItem value={p.slug} id={`cp-${p.slug}`} />
              <span>
                <span className="block font-semibold">{p.name}{p.slug === sub.plan && <span className="ml-2 text-xs text-[#D4A437]">Current</span>}</span>
                <span className="text-xs text-[#A1A1AA]">{p.contents.slice(0, 3).join(" · ")}</span>
              </span>
            </span>
            <span className="font-display font-bold">{formatGBP(p.price_pence)}</span>
          </Label>
        ))}
      </RadioGroup>
      {sel && choice !== current && (
        <>
          <Fact label="New monthly price" value={formatGBP(sel.price_pence)} testid="change-plan-new-price" />
          <Fact label="Effective from" value={fmt(sub.pause ? sub.pause.until : sub.nextBilling)} testid="change-plan-effective" />
        </>
      )}
    </Shell>
  );
};

export const CancelDialog = ({ open, onOpenChange, sub, onConfirm }) => (
  <Shell open={open} onOpenChange={onOpenChange} icon={XCircle} testid="cancel" danger title="Cancel your subscription"
    desc="We're sorry to see you go. You can cancel right here — no calls or emails needed." confirm="Confirm cancellation" onConfirm={onConfirm}>
    <Fact label="Cancellation takes effect" value={fmt(sub.nextBilling)} testid="cancel-effective-date" />
    <Fact label="Further charges" value="None" />
    <p className="text-sm leading-relaxed text-[#F7F5F0]/80" data-testid="cancel-obligations">
      You won't be charged again. Any delivery you've already paid for will still be made, and no further deliveries will be
      scheduled after that. You can change your mind before this date. We'll email you a cancellation confirmation.
    </p>
  </Shell>
);
